import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  serverTimestamp 
} from "firebase/firestore";

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("admin"); // Default to admin

  // 1. Fetch Current User Role to check for 'superadmin'
  useEffect(() => {
    const fetchRole = async () => {
      if (auth.currentUser) {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserRole(userSnap.data().role);
        }
      }
    };
    fetchRole();
  }, []);

  // 2. Real-time Payments Listener
  useEffect(() => {
    const q = query(collection(db, "payments"), orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const paymentData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayments(paymentData);
      setLoading(false);
    }, (error) => {
      console.error("Error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusUpdate = async (paymentId, newStatus) => {
    try {
      const paymentRef = doc(db, "payments", paymentId);
      await updateDoc(paymentRef, {
        status: newStatus,
        processedAt: serverTimestamp(),
      });
    } catch (error) {
      alert("Error updating status");
    }
  };

  // 3. Delete Payment Function (Superadmin Only)
  const handleDeletePayment = async (paymentId) => {
    if (window.confirm("Are you sure you want to permanently delete this payment record?")) {
      try {
        await deleteDoc(doc(db, "payments", paymentId));
        alert("Payment deleted successfully");
      } catch (error) {
        console.error("Delete error:", error);
        alert("Failed to delete. Check your security rules.");
      }
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);

  // Filter & Search Logic
  const filteredPayments = payments.filter(p => {
    const matchesStatus = filterStatus === "all" || p.status?.toLowerCase() === filterStatus.toLowerCase();
    const matchesSearch = p.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          p.accountId?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const styles = {
    container: { padding: "12px", background: "#f3f4f6", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    headerSection: { marginBottom: "32px" },
    title: { fontSize: "28px", fontWeight: "800", color: "#111827", marginBottom: "8px" },
    subtitle: { color: "#6b7280", fontSize: "14px" },
    controlsRow: { 
      display: "flex", 
      gap: "16px", 
      alignItems: "center", 
      marginTop: "20px",
      flexWrap: "wrap" 
    },
    searchInput: {
      padding: "12px 18px",
      borderRadius: "10px",
      border: "1px solid #d1d5db",
      width: "300px",
      fontSize: "14px",
      outline: "none",
      transition: "border 0.2s",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
    },
    filterSelect: { 
      padding: "12px 18px", 
      borderRadius: "10px", 
      border: "1px solid #d1d5db", 
      backgroundColor: "#fff",
      fontSize: "14px",
      fontWeight: "600",
      color: "#374151",
      outline: "none",
      cursor: "pointer",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      minWidth: "180px"
    },
    tableCard: { 
      background: "#fff", 
      borderRadius: "16px", 
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)"
    },
    gridRow: {
      display: "grid",
      // Updated grid to allow space for delete icon if superadmin
      gridTemplateColumns: userRole === "superadmin" 
        ? "2fr 1fr 1.5fr 1fr 1fr 1.2fr 0.5fr" 
        : "2.2fr 1fr 1.5fr 1.2fr 1fr 1.2fr",
      gap: "16px",
      padding: "20px 24px",
      alignItems: "center",
      borderBottom: "1px solid #f3f4f6"
    },
    amountTag: {
      backgroundColor: "#ecfdf5",
      color: "#065f46",
      padding: "6px 12px",
      borderRadius: "8px",
      fontWeight: "700",
      display: "inline-block",
      fontSize: "14px",
      textAlign: "center"
    },
    statusDropdown: (status) => {
      const colors = {
        pending: { bg: "#fffbeb", text: "#92400e", dot: "#f59e0b" },
        approved: { bg: "#ecfdf5", text: "#065f46", dot: "#10b981" },
        rejected: { bg: "#fef2f2", text: "#991b1b", dot: "#ef4444" }
      };
      const active = colors[status?.toLowerCase()] || colors.pending;
      return {
        padding: "8px 12px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: "700",
        backgroundColor: active.bg,
        color: active.text,
        border: `2px solid ${active.bg}`,
        cursor: "pointer",
        outline: "none",
        width: "100%",
        textAlign: "center",
        appearance: "none",
        backgroundImage: `radial-gradient(circle at 10% 50%, ${active.dot} 4px, transparent 5px)`
      };
    },
    deleteBtn: {
      background: "none",
      border: "none",
      color: "#ef4444",
      cursor: "pointer",
      fontSize: "18px",
      padding: "8px",
      borderRadius: "8px",
      transition: "background 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        <h2 style={styles.title}>Finance Operations</h2>
        <p style={styles.subtitle}>Review, approve, and manage global user withdrawal requests.</p>
        
        <div style={styles.controlsRow}>
          <input 
            type="text" 
            placeholder="Search email or account..." 
            style={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select 
            style={styles.filterSelect} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">📁 All Transactions</option>
            <option value="pending">⏳ Pending Review</option>
            <option value="approved">✅ Approved</option>
            <option value="rejected">❌ Rejected</option>
          </select>
        </div>
      </div>

      <div style={styles.tableCard}>
        {/* Table Header */}
        <div style={{ ...styles.gridRow, background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>User Email</span>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Method</span>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Account</span>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Amount</span>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date</span>
          <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Update Status</span>
          {userRole === "superadmin" && <span style={{ fontWeight: "700", color: "#4b5563", fontSize: "11px", textTransform: "uppercase", textAlign: "center" }}>Del</span>}
        </div>

        {loading ? (
          <div style={{ padding: "80px", textAlign: "center", color: "#6b7280" }}>
            <div className="spinner"></div>
            <p style={{ marginTop: "10px" }}>Fetching records...</p>
          </div>
        ) : filteredPayments.length > 0 ? (
          filteredPayments.map((payment) => (
            <div key={payment.id} style={styles.gridRow} className="admin-row">
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {payment.userEmail || "Anonymous User"} 
              </span>
              <span style={{ fontSize: "14px", color: "#4b5563" }}>
                {payment.withdrawalType}
              </span>
              <span style={{ fontSize: "12px", color: "#374151", fontFamily: "'Roboto Mono', monospace", background: "#f3f4f6", padding: "4px 8px", borderRadius: "4px", overflow: "hidden", textOverflow: "ellipsis" }}>
                {payment.accountId || "N/A"}
              </span>
              <div>
                <span style={styles.amountTag}>
                  {formatCurrency(payment.amount)}
                </span>
              </div>
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                {payment.timestamp?.toDate ? payment.timestamp.toDate().toLocaleDateString('en-GB') : "Processing..."}
              </span>
              
              <div>
                <select 
                  style={styles.statusDropdown(payment.status)}
                  value={payment.status || "Pending"}
                  onChange={(e) => handleStatusUpdate(payment.id, e.target.value)}
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              {/* DELETE ICON (ONLY FOR SUPERADMIN) */}
              {userRole === "superadmin" && (
                <button 
                  style={styles.deleteBtn} 
                  onClick={() => handleDeletePayment(payment.id)}
                  title="Delete Record"
                  onMouseOver={(e) => e.currentTarget.style.background = "#fee2e2"}
                  onMouseOut={(e) => e.currentTarget.style.background = "none"}
                >
                  🗑️
                </button>
              )}
            </div>
          ))
        ) : (
          <div style={{ padding: "100px", textAlign: "center", color: "#9ca3af" }}>
             <p style={{ fontSize: "18px" }}>No transactions match your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;